/**
 * SB6-DA-01 — Data-acquisition state chip, on D81's slot recipe.
 *
 * Mirrors `apps/web/.../inspection-instructions/InspectionInstructionStatusChip.tsx`
 * (the shipped SB6-II-01 chip): the same band system, now resolving through the
 * SAME shared `statusChipStyle` rather than its own copy of the fills.
 *
 * Locked state → band matrix, and the slot each band now means:
 *   white  ← CREATED                        → neutral   (inert · nothing yet)
 *   blue   ← PROCESSING, PRE_SCREENING,
 *            EXPERT_ANALYSIS                → info      (running)
 *   yellow ← EVALUATION_DONE                → attention (a human must lock it)
 *   green  ← LOCKED                         → success
 *   red    ← ISSUE, REJECTED                → critical  (told apart by label)
 *
 * TWO STATES MOVED, and both moves come out of D81:
 *
 * `EVALUATION_DONE` used to own a `violet` band of its own. Violet is RETIRED
 * from status permanently — D20 reserves `--brand-violet` for the second chart
 * series in product only, and D81 bars it from status outright. The state's
 * real meaning is "the evaluation is finished and a human must now lock it",
 * which is exactly what ATTENTION means, so that is where it goes.
 *
 * `PROCESSING` then moves off attention to INFO, so the two do not collide —
 * and info is the more accurate slot anyway: PROCESSING is literally "running".
 *
 * `data-state` + `data-color` attributes are kept for Playwright assertions.
 */
import { type ReactElement } from "react";
import { Chip } from "@mui/material";
import { statusChipStyle, type StatusSlot } from "@aeriosiq/ui";
import type { DataAcquisitionState } from "@aeriosiq/types";
import { formatInspectionState } from "./stateMapping.js";

export type DaColorBand = "white" | "yellow" | "blue" | "green" | "red";

export const DA_STATE_COLOR: Record<DataAcquisitionState, DaColorBand> = {
  CREATED: "white",
  PROCESSING: "blue",
  PRE_SCREENING: "blue",
  EXPERT_ANALYSIS: "blue",
  EVALUATION_DONE: "yellow",
  LOCKED: "green",
  ISSUE: "red",
  REJECTED: "red",
};

/** Band → D81 slot. The band is the domain vocabulary `data-color` publishes;
 *  the slot is the MEANING that carries the colour. */
const SLOT_BY_BAND: Record<DaColorBand, StatusSlot> = {
  white: "neutral",
  yellow: "attention",
  blue: "info",
  green: "success",
  red: "critical",
};

/** Resolve a state to its colour band; unknown values fall back to white.
 *  Index through a widened view so the lookup is genuinely `| undefined`
 *  (the param is an arbitrary string, not a narrowed DataAcquisitionState). */
export function daStateColorBand(state: string): DaColorBand {
  return (DA_STATE_COLOR as Record<string, DaColorBand>)[state] ?? "white";
}

export function DataAcquisitionStatusChip({
  state,
}: {
  state: string;
}): ReactElement {
  const color = daStateColorBand(state);
  return (
    <Chip
      size="small"
      label={formatInspectionState(state)}
      data-state={state}
      data-color={color}
      sx={(t) => {
        // The hue owns the fill and the keyline; the label stays neutral. Every
        // band now carries a keyline, not just the old near-transparent white.
        const chip = statusChipStyle(t, SLOT_BY_BAND[color]);
        return {
          backgroundColor: chip.backgroundColor,
          color: chip.color,
        };
      }}
    />
  );
}
